import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { streamAssistantChat } from '@/api/hooks/useAiAssistant';
import {
  loadConversations,
  newId,
  saveConversations,
  titleFromQuestion,
  type AssistantConversation,
  type AssistantMessage,
} from '@/pages/assistant/conversations';

/**
 * The WMS Analyst chat state machine, shared by the full /assistant page and
 * the floating widget: conversations (persisted per browser), the active
 * thread, and one streamed turn at a time. Views own scrolling and layout.
 */
export function useAssistantChat() {
  const [conversations, setConversations] = useState<AssistantConversation[]>(
    () => loadConversations(),
  );
  const [activeId, setActiveId] = useState<string | null>(
    () => loadConversations()[0]?.id ?? null,
  );
  // Id of the assistant message currently receiving the stream (null = idle).
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const streaming = streamingMessageId !== null;
  const abortRef = useRef<AbortController | null>(null);

  // Persist debounced — text deltas arrive per-token and a synchronous
  // localStorage serialize on every one would jank the stream. Only an
  // instance that has MUTATED its state may write: the page and the widget
  // are separate instances over one key, and a freshly mounted one must never
  // write back the snapshot it just read over the other's last turn (the
  // widget mounts while the page is still flushing its unmount save).
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!dirtyRef.current) return;
    const handle = setTimeout(() => saveConversations(conversations), 400);
    return () => clearTimeout(handle);
  }, [conversations]);
  useEffect(
    () => () => {
      // Flush the latest state on unmount, and stop any in-flight stream.
      abortRef.current?.abort();
      if (dirtyRef.current) saveConversations(conversationsRef.current);
    },
    [],
  );
  const mutate = useCallback(
    (update: (prev: AssistantConversation[]) => AssistantConversation[]) => {
      dirtyRef.current = true;
      setConversations(update);
    },
    [],
  );

  /**
   * Re-read storage. The page and the widget are separate instances over the
   * same localStorage key; whichever is shown calls this when it (re)appears
   * so it does not overwrite newer threads written by the other.
   */
  const reload = useCallback(() => {
    if (streaming) return;
    const fresh = loadConversations();
    dirtyRef.current = false;
    setConversations(fresh);
    setActiveId((current) =>
      current && fresh.some((c) => c.id === current)
        ? current
        : (fresh[0]?.id ?? null),
    );
  }, [streaming]);

  const active = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [conversations, activeId],
  );

  const sortedConversations = useMemo(
    () =>
      [...conversations].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [conversations],
  );

  const patchMessage = useCallback(
    (
      conversationId: string,
      messageId: string,
      patch: (message: AssistantMessage) => AssistantMessage,
    ) => {
      mutate((prev) =>
        prev.map((c) =>
          c.id !== conversationId
            ? c
            : {
                ...c,
                updatedAt: new Date().toISOString(),
                messages: c.messages.map((m) =>
                  m.id === messageId ? patch(m) : m,
                ),
              },
        ),
      );
    },
    [mutate],
  );

  const send = useCallback(
    (
      text: string,
      /** Re-ask a failed answer: replace that question/answer pair in place. */
      retryOf?: { conversationId: string; assistantMessageId: string },
    ) => {
      if (streaming) return;
      const now = new Date().toISOString();
      const userMessage: AssistantMessage = {
        id: newId(),
        role: 'user',
        content: text,
        at: now,
      };
      const assistantMessage: AssistantMessage = {
        id: newId(),
        role: 'assistant',
        content: '',
        toolEvents: [],
        at: now,
      };

      // Everything is computed OUTSIDE the state updater so it stays pure
      // (StrictMode double-invokes updaters; side effects there would fork ids).
      const targetId = retryOf?.conversationId ?? activeId;
      const existing = targetId
        ? conversations.find((c) => c.id === targetId)
        : null;
      const conversationId = existing?.id ?? newId();
      // On retry, drop the failed answer and the question it was for; the
      // question is re-added below so the thread reads as if it never failed.
      let keep = existing?.messages ?? [];
      if (retryOf && existing) {
        const failedAt = keep.findIndex(
          (m) => m.id === retryOf.assistantMessageId,
        );
        if (failedAt > 0 && keep[failedAt - 1].role === 'user') {
          keep = keep.slice(0, failedAt - 1);
        } else if (failedAt >= 0) {
          keep = keep.slice(0, failedAt);
        }
      }
      const kept = keep;
      const history = kept
        .filter((m) => m.content.trim().length > 0 && !m.error)
        .map((m) => ({ role: m.role, content: m.content }));

      mutate((prev) => {
        const target = prev.find((c) => c.id === conversationId);
        if (target) {
          return prev.map((c) =>
            c.id !== conversationId
              ? c
              : {
                  ...c,
                  updatedAt: now,
                  messages: [
                    ...(retryOf ? kept : c.messages),
                    userMessage,
                    assistantMessage,
                  ],
                },
          );
        }
        const fresh: AssistantConversation = {
          id: conversationId,
          title: titleFromQuestion(text),
          createdAt: now,
          updatedAt: now,
          messages: [userMessage, assistantMessage],
        };
        return [fresh, ...prev];
      });
      setActiveId(conversationId);
      setStreamingMessageId(assistantMessage.id);

      const controller = new AbortController();
      abortRef.current = controller;
      const cid = conversationId;
      const mid = assistantMessage.id;

      void streamAssistantChat(
        [...history, { role: 'user', content: text }],
        {
          onText: (delta) =>
            patchMessage(cid, mid, (m) => ({
              ...m,
              content: m.content + delta,
            })),
          onMeta: (meta) =>
            patchMessage(cid, mid, (m) => ({ ...m, model: meta.model })),
          onNote: (note) =>
            patchMessage(cid, mid, (m) => ({
              ...m,
              toolEvents: [
                ...(m.toolEvents ?? []),
                {
                  id: newId(),
                  name: 'note',
                  input: note,
                  status: 'done' as const,
                },
              ],
            })),
          onThinking: (delta) =>
            patchMessage(cid, mid, (m) => ({
              ...m,
              thinking: (m.thinking ?? '') + delta,
            })),
          onToolStart: (event) =>
            patchMessage(cid, mid, (m) => ({
              ...m,
              toolEvents: [
                ...(m.toolEvents ?? []),
                { ...event, status: 'running' as const },
              ],
            })),
          onToolResult: (event) =>
            patchMessage(cid, mid, (m) => ({
              ...m,
              toolEvents: (m.toolEvents ?? []).map((t) =>
                t.id === event.id
                  ? {
                      ...t,
                      status: event.ok ? ('done' as const) : ('error' as const),
                      summary: event.summary,
                      durationMs: event.durationMs,
                    }
                  : t,
              ),
            })),
          onRound: () => undefined,
          onDone: (event) => {
            patchMessage(cid, mid, (m) => ({
              ...m,
              stats: {
                durationMs: event.durationMs,
                rounds: event.rounds,
                outputTokens: event.usage.outputTokens,
              },
            }));
          },
          onError: (message) => {
            patchMessage(cid, mid, (m) => ({ ...m, error: message }));
          },
        },
        controller.signal,
      ).finally(() => {
        setStreamingMessageId((current) => (current === mid ? null : current));
        // Normalize any spinner left by an abort mid-tool.
        patchMessage(cid, mid, (m) => ({
          ...m,
          toolEvents: (m.toolEvents ?? []).map((t) =>
            t.status === 'running' ? { ...t, status: 'error' as const } : t,
          ),
        }));
      });
    },
    [activeId, conversations, patchMessage, streaming],
  );

  /** Ask the failed question again, in place. */
  const retry = useCallback(
    (conversationId: string, assistantMessageId: string) => {
      const conv = conversations.find((c) => c.id === conversationId);
      const at =
        conv?.messages.findIndex((m) => m.id === assistantMessageId) ?? -1;
      const question = at > 0 ? conv!.messages[at - 1] : undefined;
      if (!question || question.role !== 'user') return;
      send(question.content, { conversationId, assistantMessageId });
    },
    [conversations, send],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setStreamingMessageId(null);
  }, []);

  const newConversation = useCallback(() => setActiveId(null), []);

  /** Remember the thumbs a person gave an answer (the server has the record). */
  const setFeedback = useCallback(
    (conversationId: string, messageId: string, feedback: 'UP' | 'DOWN') =>
      patchMessage(conversationId, messageId, (m) => ({ ...m, feedback })),
    [patchMessage],
  );

  const deleteConversation = useCallback(
    (id: string) => {
      const target = conversations.find((c) => c.id === id);
      const holdsStream =
        !!streamingMessageId &&
        !!target?.messages.some((m) => m.id === streamingMessageId);
      mutate((prev) => prev.filter((c) => c.id !== id));
      if (holdsStream) {
        abortRef.current?.abort();
        setStreamingMessageId(null);
      }
      if (activeId === id) setActiveId(null);
    },
    [activeId, conversations, streamingMessageId, mutate],
  );

  return {
    conversations: sortedConversations,
    active,
    activeId,
    selectConversation: setActiveId,
    newConversation,
    deleteConversation,
    setFeedback,
    streamingMessageId,
    streaming,
    send,
    retry,
    stop,
    reload,
  };
}
